import {
  Button,
  fade,
  Grid,
  makeStyles,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@material-ui/core";
import { Autocomplete, AutocompleteCloseReason } from "@material-ui/lab";
import React, { useEffect } from "react";
import { DeleteAttributeCardComponent } from "../../components/DeleteAttributeCard";
import { appColor } from "../../constants/customThemeColor";
import { useCustomStyle } from "../../../styles";
import {
  AddNewButtonProps,
  ResponseScaleProp,
  ShareFeedbackAttributeNameAndId,
  State,
} from "../../../type";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import {
  RenderAttributeQuestion,
  RenderLikertNaScaleAnswer,
  RenderLikertScaleAnswer,
  RenderResources,
  RenderStringAnswer,
  RenderSuggestions,
  RenderContext,
  EditorForText,
  SbiChips,
} from "./addNewButtonElements";
import { updateEmojiAndAttributeOnClick } from "./addNewButtonHandles";
import { AttributeTitleComponent } from "./AttributeTitle";
import { VriddhiResponseOptions } from "./Vriddhi/VriddhiResponseOptions";
import { useSelector } from "react-redux";
import { EditorState } from "draft-js";
import { buildExplanationStates, isExplanationEmpty, editorStateToHtml } from "../explanationHelpers";
import ToastMessage, { MESSAGE_TYPE } from "../../components/ToastMessage";
import { useSbiAnalysis } from "./useSbiAnalysis";

const useStyle = makeStyles((theme) => ({
  popper: {
    border: "1px solid rgba(27,31,35,.15)",
    boxShadow: "0 3px 12px rgba(27,31,35,.15)",
    borderRadius: 3,
    width: 300,
    zIndex: 1,
    fontSize: 13,
    color: "#586069",
    backgroundColor: "#f6f8fa",
  },
  textField: {
    padding: 10,
    width: "100%",
    borderBottom: "1px solid #dfe2e5",
    "& input": {
      borderRadius: 4,
      backgroundColor: theme.palette.common.white,
      padding: 8,
      transition: theme.transitions.create(["border-color", "box-shadow"]),
      border: "1px solid #ced4da",
      "&:focus": {
        boxShadow: `${fade(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
        borderColor: theme.palette.primary.main,
      },
    },
  },
  paper: {
    width: "100%",
    padding: "0px 0px 10px 0px",
    marginTop: "20px",
    marginBottom: "20px",
    textAlign: "start",
  },
  paper2: {
    padding: "0px 0px 10px 0px",
    margin: theme.spacing(3.5, 3, 3.5, 3),
    textAlign: "start",
    width: "-webkit-fill-available",
  },
  btn: {
    margin: theme.spacing(3, 0, 0, 0),
    backgroundColor: `${appColor.regularBackgroundColor}`,
    zIndex: 50,
  },
  btn2: {
    margin: theme.spacing(5, 0, 5, 3),
    backgroundColor: "#576BEA",
    padding: theme.spacing(1.5),
  },
  typography_body_1: {
    alignItems: "center",
    color: `${appColor.pureWhite}`,
    [theme.breakpoints.down("sm")]: {
      fontSize: "10px",
    },
  },
  typography_body_3: {
    marginLeft: theme.spacing(0.5),
    alignItems: "center",
    color: `${appColor.pureBlack}`,
    [theme.breakpoints.down("sm")]: {
      fontSize: "10px",
    },
  },
  skipCTA: {
    fontSize: theme.spacing(1.75),
    fontFamily: "Montserrat",
    fontWeight: 600,
    color: "#576BEA",
    textDecoration: "underline",
    margin: theme.spacing(0.25, 1.5, 0, 0),
    [theme.breakpoints.down("xs")]: {
      fontSize: theme.spacing(1.25),
      margin: theme.spacing(0.25, 0, 0, 0),
    },
  },
}));

const MIN_WORDS_FOR_ANALYSIS = 10;

const wordCount = (editorState?: EditorState): number => {
  if (!editorState) return 0;
  const text = editorState.getCurrentContent().getPlainText().trim();
  return text === "" ? 0 : text.split(/\s+/).length;
};

export const AddNewButton: React.FC<AddNewButtonProps> = ({
  attributes,
  setAttributes,
  shareFeedbackData,
  setShareFeedbackData,
  emojiSelected,
  setEmojiSelected,
  setValue,
  onAnyChange,
  isMissing,
  attLabels,
  from,
}) => {
  const NO_SELECTION = -1;
  const classes = useStyle();
  const customClasses = useCustomStyle();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [onDeleteAttribute, setOnDeleteAttribute] = React.useState<number>(0);
  const [questionExplanationStates, setQuestionExplanationStates] = React.useState<
    Array<{ explanations: Array<EditorState> }>
  >([]);
  const {
    aiHistoryMap,
    activeSbiMap,
    analyzingMap,
    aiBaselineTextMap,
    errorToast,
    dismissError,
    analyze,
    undo,
    clearSbiChips,
  } = useSbiAnalysis();

  const handleAnalyze = async (fieldKey: string, rawText: string, attributeIdx: number, questionIdx: number) => {
    const result = await analyze(fieldKey, rawText);
    if (!result) return;

    const newStates = [...questionExplanationStates];
    if (!newStates[attributeIdx]) newStates[attributeIdx] = { explanations: [] };
    newStates[attributeIdx].explanations[questionIdx] = result.editorState;
    setQuestionExplanationStates(newStates);

    const updatedData = [...shareFeedbackData];
    updatedData[attributeIdx] = { ...updatedData[attributeIdx], questions: [...updatedData[attributeIdx].questions] };
    updatedData[attributeIdx].questions[questionIdx] = { ...updatedData[attributeIdx].questions[questionIdx], explanation: result.restructuredText };
    setShareFeedbackData(updatedData);

    if (onAnyChange) onAnyChange(true);
  };

  const handleUndo = (fieldKey: string, attributeIdx: number, questionIdx: number) => {
    const result = undo(fieldKey);
    if (!result) return;

    const newStates = [...questionExplanationStates];
    newStates[attributeIdx].explanations[questionIdx] = result.editorState;
    setQuestionExplanationStates(newStates);

    const updatedData = [...shareFeedbackData];
    updatedData[attributeIdx].questions[questionIdx] = { ...updatedData[attributeIdx].questions[questionIdx], explanation: result.text };
    setShareFeedbackData(updatedData);

    if (onAnyChange) onAnyChange(true);
  };

  const handleEditorChange = (attributeIdx: number, questionIdx: number, state: EditorState) => {
    const html = editorStateToHtml(state);
    const updatedData = [...shareFeedbackData];
    updatedData[attributeIdx] = { ...updatedData[attributeIdx], questions: [...updatedData[attributeIdx].questions] };
    updatedData[attributeIdx].questions[questionIdx] = { ...updatedData[attributeIdx].questions[questionIdx], explanation: html };
    setShareFeedbackData(updatedData);

    const newStates = [...questionExplanationStates];
    if (!newStates[attributeIdx]) {
      newStates[attributeIdx] = { explanations: [] };
    }
    newStates[attributeIdx].explanations[questionIdx] = state;
    setQuestionExplanationStates(newStates);

    const fieldKey = `${attributeIdx}-${questionIdx}`;
    const currentPlain = state.getCurrentContent().getPlainText();
    clearSbiChips(fieldKey, currentPlain);

    if (onAnyChange) onAnyChange(true);
  };

  useEffect(() => {
    if (shareFeedbackData.length > 0 && questionExplanationStates.length !== shareFeedbackData.length) {
      setQuestionExplanationStates(buildExplanationStates(shareFeedbackData, questionExplanationStates));
    }
  }, [shareFeedbackData]);

  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const [deletingAttibute, setDeletingAttribute] =
    React.useState<ShareFeedbackAttributeNameAndId>({
      attributeId: 0,
      attributeName: "",
    });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDelete = (id: number) => {
    setAttributes(attributes.filter((attribute) => attribute.id !== id));
    setQuestionExplanationStates([]);
    handleModalClose();
    setOnDeleteAttribute(1);
  };

  useEffect(() => {
    setAttributes(attributes);
    if (onDeleteAttribute === 1) {
      updateEmojiAndAttributeOnClick(
        attributes,
        emojiSelected,
        shareFeedbackData,
        setEmojiSelected,
        setShareFeedbackData
      );
    }
    setOnDeleteAttribute(0);
  }, [onDeleteAttribute]);

  const handleClose = (
    event: React.ChangeEvent<{}>,
    reason: AutocompleteCloseReason
  ) => {
    if (reason === "toggleInput") {
      return;
    }
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleModalOpen = () => {
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
  };

  const renderDeletionModal = () => (
    <DeleteAttributeCardComponent
      openModal={openModal}
      deletingAttribute={deletingAttibute}
      handleDelete={handleDelete}
      handleModalClose={handleModalClose}
    />
  );
  let regex = /(<([^>]+)>)/gi;

  const emptyList: Array<ResponseScaleProp> = [];
  const responseScales =
    useSelector((state: State) => state.data.data?.scaleResponseEntityList) ??
    emptyList;

  const responseScales2 =
    useSelector(
      (state: State) =>
        state.attributesDataForClient.data?.scaleResponseEntityList
    ) ?? emptyList;

  const clearQuestionResponse = (attributeIdx, questionIdx) => {
    const updatedEmojiSelected = [...emojiSelected];
    const attribute = updatedEmojiSelected[attributeIdx];
    if (attribute && attribute.queEmoji[questionIdx]) {
      const scaleName = shareFeedbackData[attributeIdx]?.questions[questionIdx]?.scaleName;
      attribute.queEmoji[questionIdx].emojiId = scaleName === "VRIDDHI" ? [] : NO_SELECTION;
      setEmojiSelected(updatedEmojiSelected);
    }
  };

  return (
    <>
      <Grid container alignItems="flex-start" direction="column">
        {shareFeedbackData.map((attribute, idx) => (
          <Paper
            variant="outlined"
            className={from === "EMPLOYEE" ? classes.paper : classes.paper2}
            key={attribute.attributeId}
            id={attribute.attributeId.toString()}
          >
            <div key={attribute.attributeId}>
              {
                <AttributeTitleComponent
                  attribute={attribute}
                  setDeletingAttribute={setDeletingAttribute}
                  handleModalOpen={handleModalOpen}
                />
              }
              {attribute.questions.map((question, idx_1) => (
                <Grid
                  key={question.questionId}
                  id={`que-id-${question.questionId}`}
                  container
                  alignItems="flex-start"
                  style={
                    question.answer
                      .replaceAll(/\&nbsp;/g, "")
                      .replaceAll(regex, "")
                      .replace(/\s/g, "")
                      .replace(/\n/g, "").length === 0 && isMissing
                      ? { backgroundColor: "#FFF4EB" }
                      : {}
                  }
                >
                  <Grid
                    item
                    xs
                    container
                    justify="flex-start"
                  >
                    {
                      <RenderAttributeQuestion
                        question={question.questionName}
                        answer={question.answer}
                      ></RenderAttributeQuestion>
                    }

                    {/* Emoticons */}
                    {question.scaleName === "LIKERT" && (
                      <RenderLikertScaleAnswer
                        idx={idx}
                        idx_1={idx_1}
                        emojiSelected={emojiSelected}
                        setEmojiSelected={setEmojiSelected}
                        shareFeedbackData={shareFeedbackData}
                        setShareFeedbackData={setShareFeedbackData}
                        scaleVersion={question.scaleVersion}
                      ></RenderLikertScaleAnswer>
                    )}

                    {/* Free Text Field */}
                    {question.scaleName === "STRING" && (
                      <RenderStringAnswer
                        question={question}
                        idx={idx}
                        idx_1={idx_1}
                        shareFeedbackData={shareFeedbackData}
                        setShareFeedbackData={setShareFeedbackData}
                        onAnyChange={onAnyChange}
                      ></RenderStringAnswer>
                    )}

                    {/* Emoticons with NA */}
                    {question.scaleName === "LIKERT_NA" && (
                      <RenderLikertNaScaleAnswer
                        idx={idx}
                        idx_1={idx_1}
                        emojiSelected={emojiSelected}
                        setEmojiSelected={setEmojiSelected}
                        shareFeedbackData={shareFeedbackData}
                        setShareFeedbackData={setShareFeedbackData}
                        scaleVersion={question.scaleVersion}
                      ></RenderLikertNaScaleAnswer>
                    )}

                    {/* Vriddhi */}
                    {question.scaleName === "VRIDDHI" && (
                      <VriddhiResponseOptions
                        idx={idx}
                        idx_1={idx_1}
                        emojiSelected={emojiSelected}
                        setEmojiSelected={setEmojiSelected}
                        shareFeedbackData={shareFeedbackData}
                        setShareFeedbackData={setShareFeedbackData}
                        scaleVersion={question.scaleVersion}
                        responseScales={responseScales}
                        responseScales2={responseScales2}
                      ></VriddhiResponseOptions>
                    )}

                    {/* Explanation Field */}
                    <Grid item xs={12} style={{ marginTop: 16, marginBottom: 16, paddingLeft: 20, paddingRight: 20 }}>
                      <Typography gutterBottom variant="h5">
                        {"Explanation/Details "}
                        {isExplanationEmpty(question.explanation) && (
                        <span
                          style={{ color: `${appColor.redWarn}` }}
                          className={customClasses.typography_font_14_8_500}
                        >
                          (required)
                        </span>
                        )}
                      </Typography>
                      <EditorForText
                        idx={idx * 1000 + idx_1}
                        textHeader="explanation"
                        editorState={
                          questionExplanationStates[idx]?.explanations[idx_1] ||
                          EditorState.createEmpty()
                        }
                        onEditorStateChange={(state) => handleEditorChange(idx, idx_1, state)}
                        placeHolderText="Provide detailed explanation for this question"
                      />
                      <Grid container alignItems="center" style={{ marginTop: 8 }}>
                        <Grid item>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            disabled={
                              analyzingMap[`${idx}-${idx_1}`] ||
                              wordCount(questionExplanationStates[idx]?.explanations[idx_1]) < MIN_WORDS_FOR_ANALYSIS
                            }
                            onClick={() => {
                              const plainText = (questionExplanationStates[idx]?.explanations[idx_1] || EditorState.createEmpty())
                                .getCurrentContent()
                                .getPlainText();
                              handleAnalyze(`${idx}-${idx_1}`, plainText, idx, idx_1);
                            }}
                          >
                            {analyzingMap[`${idx}-${idx_1}`] ? "Analyzing..." : "✨ Improve My Feedback"}
                          </Button>
                        </Grid>
                        {(aiHistoryMap[`${idx}-${idx_1}`]?.length ?? 0) > 0 && (
                          <Grid item style={{ marginLeft: 12 }}>
                            <Button
                              size="small"
                              onClick={() => handleUndo(`${idx}-${idx_1}`, idx, idx_1)}
                              style={{ color: "#576BEA", textDecoration: "underline" }}
                            >
                              Undo AI Changes
                            </Button>
                          </Grid>
                        )}
                      </Grid>
                      {activeSbiMap[`${idx}-${idx_1}`] && (
                        <SbiChips
                          situationPresent={activeSbiMap[`${idx}-${idx_1}`]!.situationPresent}
                          behaviorPresent={activeSbiMap[`${idx}-${idx_1}`]!.behaviorPresent}
                          impactPresent={activeSbiMap[`${idx}-${idx_1}`]!.impactPresent}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              ))}
              {attribute.questions.length === 0 && (
                <RenderContext
                  attribute={attribute}
                  idx={idx}
                  shareFeedbackData={shareFeedbackData}
                  setShareFeedbackData={setShareFeedbackData}
                  setValue={setValue}
                  onAnyChange={onAnyChange}
                  isMissing={isMissing}
                ></RenderContext>
              )}
              {
                <RenderSuggestions
                  attribute={attribute}
                  idx={idx}
                  shareFeedbackData={shareFeedbackData}
                  setShareFeedbackData={setShareFeedbackData}
                  setValue={setValue}
                  onAnyChange={onAnyChange}
                ></RenderSuggestions>
              }
              {
                <RenderResources
                  attribute={attribute}
                  idx={idx}
                  shareFeedbackData={shareFeedbackData}
                  setShareFeedbackData={setShareFeedbackData}
                  setValue={setValue}
                  onAnyChange={onAnyChange}
                ></RenderResources>
              }
            </div>
          </Paper>
        ))}
      </Grid>
      <Grid container justify="center" alignItems="center">
        {renderDeletionModal()}
      </Grid>
      <ToastMessage
        open={!!errorToast}
        handleClose={dismissError}
        message={errorToast ?? ""}
        type={MESSAGE_TYPE.INFO}
      />
      <Button
        variant="contained"
        color="primary"
        onClick={handleClick}
        className={from === "EMPLOYEE" ? classes.btn : classes.btn2}
        endIcon={from === "CLIENT" && <ArrowDropDownIcon />}
      >
        <Typography variant="h2" className={classes.typography_body_1}>
          ADD NEW
        </Typography>
      </Button>
      <Popper
        id="popper"
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        className={classes.popper}
      >
        <Autocomplete
          open
          onClose={handleClose}
          filterSelectedOptions
          multiple
          value={attributes}
          onChange={(event, newValue) => {
            setAttributes(newValue);
            updateEmojiAndAttributeOnClick(
              newValue,
              emojiSelected,
              shareFeedbackData,
              setEmojiSelected,
              setShareFeedbackData
            );
          }}
          disablePortal
          renderOption={(option) => (
            <React.Fragment>
              <div className={classes.typography_body_3}>
                <Typography variant="h5" className={classes.typography_body_3}>
                  {option.name}
                </Typography>
              </div>
            </React.Fragment>
          )}
          options={attLabels}
          getOptionLabel={(option) => option.name}
          getOptionSelected={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              placeholder="Search attribute by name "
              variant="outlined"
              ref={params.InputProps.ref}
              autoFocus
              inputProps={params.inputProps}
              className={`${customClasses.typography_font_14_10} ${classes.textField}`}
            />
          )}
        />
      </Popper>
    </>
  );
};
